import { useCallback, useEffect, useState } from 'react';
import { Badge, Banner, BlockStack, Box, Button, ButtonGroup, Checkbox, DataTable, FormLayout, InlineStack, Modal, Select, Text, TextField } from '@shopify/polaris';
import { useLookupStore } from '../../../context/useStore';
import { getRequest } from '../../../shared/components/functions';
import DetailFileBrowser from '../../../shared/components/files/DetailFileBrowser';

const EPSILON = 0.0001;

const PURCHASE_STATUS_IDS = {
  DRAFT: 1,
  SENT: 2,
  PARTIALLY_RECEIVED: 3,
  RECEIVED: 4,
  CANCELLED: 5
};

function deriveStatusId(purchase = {}) {
  if (purchase.PurchaseStatusID) return parseInt(purchase.PurchaseStatusID, 10);
  if (purchase.Geannuleerd) return PURCHASE_STATUS_IDS.CANCELLED;
  if (purchase.Ontvangen) return PURCHASE_STATUS_IDS.RECEIVED;
  if (purchase.Verstuurd) return PURCHASE_STATUS_IDS.SENT;
  return PURCHASE_STATUS_IDS.DRAFT;
}

function formatDateTime(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch (_err) {
    return String(value);
  }
}

function formatQty(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '0';
  const rounded = Math.round(parsed * 1000) / 1000;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace(/\.?0+$/, '');
}

function statusBadge(statusId, statusLabel) {
  if (statusId === PURCHASE_STATUS_IDS.RECEIVED) return <Badge tone="success">{statusLabel || 'Received'}</Badge>;
  if (statusId === PURCHASE_STATUS_IDS.PARTIALLY_RECEIVED) return <Badge tone="attention">{statusLabel || 'Partially received'}</Badge>;
  if (statusId === PURCHASE_STATUS_IDS.SENT) return <Badge tone="info">{statusLabel || 'Sent'}</Badge>;
  if (statusId === PURCHASE_STATUS_IDS.CANCELLED) return <Badge tone="critical">{statusLabel || 'Cancelled'}</Badge>;
  return <Badge>{statusLabel || 'Draft'}</Badge>;
}

// rendered inside the tabbed side card of the purchase order page
export default function PurchaseReceivingPanel({ purchaseId, purchase, company, onReceiptCreated }) {
  const pickingplaces = useLookupStore((state) => state.pickingplaces);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [payload, setPayload] = useState({ lines: [], receipts: [], summary: null });
  const [modalOpen, setModalOpen] = useState(false);
  const [receiveLines, setReceiveLines] = useState([]);
  const [notes, setNotes] = useState('');
  const [applyStock, setApplyStock] = useState(true);

  const statusId = deriveStatusId(purchase);
  const canReceive = statusId === PURCHASE_STATUS_IDS.SENT || statusId === PURCHASE_STATUS_IDS.PARTIALLY_RECEIVED;

  const getStockplaceName = useCallback(
    (stockplaatsId, fallbackName = null) => {
      if (fallbackName) return fallbackName;
      if (stockplaatsId === null || stockplaatsId === undefined || stockplaatsId === '') return '-';
      const stockplaats = Number(stockplaatsId);
      if (Number.isNaN(stockplaats)) return '-';
      const match = (pickingplaces || []).find((place) => Number(place.counter) === stockplaats);
      return match?.pickingplace || `Location ${stockplaats}`;
    },
    [pickingplaces]
  );

  const getLineStockplaceOptions = useCallback(
    (line) => {
      const options = [];
      const seen = new Set();

      const addOption = (value, label) => {
        if (value === null || value === undefined || value === '') return;
        const normalized = String(value);
        if (seen.has(normalized)) return;
        options.push({ label, value: normalized });
        seen.add(normalized);
      };

      (line.ExistingStockplaatsen || []).forEach((location) => {
        const name = getStockplaceName(location.Stockplaats, location.pickingplace);
        addOption(location.Stockplaats, `${name} (in stock: ${formatQty(location.CurrentQty)})`);
      });

      (pickingplaces || []).forEach((place) => {
        addOption(place.counter, place.pickingplace);
      });

      const suggestedStockplaatsId = line.InitialSuggestedStockplaatsID ?? line.SuggestedStockplaatsID;
      if (suggestedStockplaatsId !== null && suggestedStockplaatsId !== undefined && suggestedStockplaatsId !== '') {
        const suggestedName = getStockplaceName(suggestedStockplaatsId, line.InitialSuggestedStockplaatsName || line.SuggestedStockplaatsName || null);
        if (!seen.has(String(suggestedStockplaatsId))) addOption(suggestedStockplaatsId, suggestedName);
        options.forEach((option) => {
          if (option.value === String(suggestedStockplaatsId) && !option.label.startsWith('Suggested ->')) {
            option.label = `Suggested -> ${option.label}`;
          }
        });
      }

      if (!options.length) options.push({ label: 'No stockplace available', value: '' });
      return options;
    },
    [getStockplaceName, pickingplaces]
  );

  const getInitialStockplaatsValue = useCallback(
    (line) => {
      const existing = Array.isArray(line.ExistingStockplaatsen) ? line.ExistingStockplaatsen : [];
      const withInventory = existing
        .filter((location) => {
          const stockplaatsId = location?.Stockplaats;
          return stockplaatsId !== null && stockplaatsId !== undefined && stockplaatsId !== '' && Number(location?.CurrentQty || 0) > EPSILON;
        })
        .sort((a, b) => Number(b?.CurrentQty || 0) - Number(a?.CurrentQty || 0));
      if (withInventory.length > 0) {
        return String(withInventory[0].Stockplaats);
      }

      if (line.SuggestedStockplaatsID !== null && line.SuggestedStockplaatsID !== undefined && line.SuggestedStockplaatsID !== '') {
        return String(line.SuggestedStockplaatsID);
      }

      const firstExisting = existing.find((location) => location.Stockplaats !== null && location.Stockplaats !== undefined && location.Stockplaats !== '');
      if (firstExisting) return String(firstExisting.Stockplaats);

      const allPlaces = pickingplaces || [];
      const kastLookup = allPlaces.find((place) => Number(place.counter) === 1);
      if (kastLookup && kastLookup.counter !== null && kastLookup.counter !== undefined && kastLookup.counter !== '') {
        return String(kastLookup.counter);
      }

      if (allPlaces.length > 0) return String(allPlaces[0].counter);
      return '';
    },
    [pickingplaces]
  );

  const refresh = useCallback(async () => {
    if (!purchaseId || purchaseId === 'new') return;
    setLoading(true);
    setErrorMessage('');
    try {
      const data = await getRequest(`/api/purchases/${purchaseId}/receiving`);
      if (data) setPayload(data);
    } catch (err) {
      setErrorMessage('Failed to load receiving data.');
    } finally {
      setLoading(false);
    }
  }, [purchaseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openReceiveModal = () => {
    const draftLines = (payload?.lines || []).map((line) => ({
      BestellingLijnID: line.BestellingLijnID,
      produktnr: line.produktnr,
      tekstlijn: line.tekstlijn,
      OrderedQty: Number(line.aantal || 0),
      ReceivedQty: Number(line.ReceivedQty || 0),
      RemainingQty: Number(line.RemainingQty || 0),
      receiveQty: Number(line.RemainingQty || 0),
      SuggestedStockplaatsID: getInitialStockplaatsValue(line),
      InitialSuggestedStockplaatsID: line.SuggestedStockplaatsID ?? null,
      InitialSuggestedStockplaatsName: line.SuggestedStockplaatsName || '',
      SuggestedStockplaatsName: line.SuggestedStockplaatsName || '',
      ExistingStockplaatsen: Array.isArray(line.ExistingStockplaatsen) ? line.ExistingStockplaatsen : []
    }));
    setReceiveLines(draftLines);
    setNotes('');
    setApplyStock(true);
    setModalOpen(true);
  };

  const updateReceiveLine = (index, key, value) => {
    setReceiveLines((prev) =>
      prev.map((line, rowIndex) => {
        if (rowIndex !== index) return line;
        if (key === 'receiveQty') {
          if (value === '') return { ...line, receiveQty: '' };
          const parsed = Number(value);
          return { ...line, receiveQty: Number.isFinite(parsed) ? parsed : 0 };
        }
        return { ...line, [key]: value };
      })
    );
  };

  const handleSubmitReceipt = async () => {
    const normalizedLines = receiveLines
      .map((line) => {
        const qty = Number(line.receiveQty || 0);
        return {
          BestellingLijnID: line.BestellingLijnID,
          ReceivedQty: Number.isFinite(qty) ? qty : 0,
          SuggestedStockplaatsID: line.SuggestedStockplaatsID === '' || line.SuggestedStockplaatsID === null || line.SuggestedStockplaatsID === undefined ? null : Number(line.SuggestedStockplaatsID),
          RemainingQty: Number(line.RemainingQty || 0)
        };
      })
      .filter((line) => line.ReceivedQty > 0);

    if (!normalizedLines.length) {
      setErrorMessage('Select at least one line quantity to receive.');
      return;
    }

    const invalidLine = normalizedLines.find((line) => line.ReceivedQty - line.RemainingQty > EPSILON);
    if (invalidLine) {
      setErrorMessage('One or more receiving quantities exceed the remaining quantity.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch(`/api/purchases/${purchaseId}/receipts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lines: normalizedLines,
          notes: notes || null,
          applyStock
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.msg || 'Failed to create receipt.');
      }

      setPayload(data);
      setModalOpen(false);
      if (typeof onReceiptCreated === 'function') onReceiptCreated(data);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to create receipt.');
    } finally {
      setSubmitting(false);
    }
  };

  const summary = payload?.summary || {};
  const receipts = payload?.receipts || [];

  const receiptRows = receipts.map((receipt) => [
    formatDateTime(receipt.ReceivedAt),
    receipt.Afkorting || receipt.Gebruikernaam || '-',
    receipt.LineCount || 0,
    receipt.TotalReceivedQty || 0,
    receipt.StockApplied ? 'Yes' : 'No',
    receipt.LeadTimeHours != null ? `${receipt.LeadTimeHours} h` : '-',
    receipt.Notes || '-'
  ]);

  return (
    <>
      <BlockStack gap="300">
        {errorMessage ? <Banner tone="critical">{errorMessage}</Banner> : null}

        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text as="span" variant="bodySm" tone="subdued">
              Status:
            </Text>
            {statusBadge(statusId, purchase?.PurchaseStatusName)}
          </InlineStack>
          <ButtonGroup>
            <Button variant="plain" onClick={refresh} disabled={loading || submitting}>
              Refresh
            </Button>
            <Button onClick={openReceiveModal} disabled={!canReceive || loading || submitting}>
              Receive items
            </Button>
          </ButtonGroup>
        </InlineStack>

        <InlineStack gap="400">
          <Text as="p" variant="bodySm">
            Ordered: <strong>{summary.totalOrderedQty || 0}</strong>
          </Text>
          <Text as="p" variant="bodySm">
            Received: <strong>{summary.totalReceivedQty || 0}</strong>
          </Text>
          <Text as="p" variant="bodySm">
            Remaining: <strong>{summary.totalRemainingQty || 0}</strong>
          </Text>
          <Text as="p" variant="bodySm">
            Avg lead time: <strong>{summary.avgLeadTimeHours != null ? `${summary.avgLeadTimeHours} h` : '-'}</strong>
          </Text>
        </InlineStack>

        {receipts.length > 0 ? (
          <DataTable
            verticalAlign="middle"
            increasedTableDensity
            columnContentTypes={['text', 'text', 'numeric', 'numeric', 'text', 'text', 'text']}
            headings={['Received at', 'By', 'Lines', 'Qty', 'Stock applied', 'Lead time', 'Notes']}
            rows={receiptRows}
          />
        ) : (
          <Text as="p" tone="subdued">
            No receipts registered yet.
          </Text>
        )}

        {purchaseId !== 'new' && company?.Klantnummer ? (
          <Box>
            <Text as="p" variant="bodySm" tone="subdued">
              Attach packing slips or warehouse receiving files:
            </Text>
            <DetailFileBrowser
              docTypeId={13}
              resourceId={purchaseId}
              Klantnummer={company.Klantnummer}
              docNumber={purchase?.Bestellingnummer}
              FIRNR={company.FIRNR}
              docType="order"
              heightPxs="220px"
              parentRefetchDocuments={refresh}
            />
          </Box>
        ) : null}
      </BlockStack>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Receive purchase order"
        size="large"
        primaryAction={{ content: 'Save receipt', onAction: handleSubmitReceipt, loading: submitting }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setModalOpen(false), disabled: submitting }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <FormLayout>
              <Checkbox checked={applyStock} onChange={setApplyStock} label="Apply stock increase immediately" />
              <TextField label="Notes" value={notes} onChange={setNotes} autoComplete="off" multiline={3} />
            </FormLayout>
            <Text as="p" tone="subdued" variant="bodySm">
              Current stockplaatsen show where this part already exists. Choose &quot;-&gt; Transfer to&quot; to decide where the received quantity should go.
            </Text>

            <div style={{ maxHeight: '380px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', paddingBottom: '8px' }}>Code</th>
                    <th style={{ textAlign: 'left', paddingBottom: '8px' }}>Description</th>
                    <th style={{ textAlign: 'right', paddingBottom: '8px' }}>Remaining</th>
                    <th style={{ textAlign: 'right', paddingBottom: '8px', width: '120px' }}>Receive qty</th>
                    <th style={{ textAlign: 'left', paddingBottom: '8px', width: '260px' }}>Current stockplaatsen</th>
                    <th style={{ textAlign: 'left', paddingBottom: '8px', width: '280px' }}>-&gt; Transfer to</th>
                  </tr>
                </thead>
                <tbody>
                  {receiveLines.map((line, index) => {
                    const productId = Number(line.produktnr || 0);
                    const productUrl = Number.isInteger(productId) && productId > 0 ? `/products/${productId}` : null;

                    return (
                      <tr key={line.BestellingLijnID}>
                        <td style={{ padding: '6px 8px 6px 0' }}>
                          {productUrl ? (
                            <a href={productUrl} target="_blank" rel="noreferrer" className="Polaris-Link">
                              #{line.produktnr}
                            </a>
                          ) : (
                            `#${line.produktnr}`
                          )}
                        </td>
                        <td style={{ padding: '6px 8px 6px 0' }}>
                          {productUrl ? (
                            <a href={productUrl} target="_blank" rel="noreferrer" className="Polaris-Link">
                              {line.tekstlijn}
                            </a>
                          ) : (
                            line.tekstlijn
                          )}
                        </td>
                        <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>{line.RemainingQty}</td>
                        <td style={{ padding: '6px 8px 6px 0' }}>
                          <TextField
                            label=""
                            autoComplete="off"
                            type="number"
                            value={line.receiveQty === '' ? '' : String(line.receiveQty)}
                            onChange={(value) => updateReceiveLine(index, 'receiveQty', value)}
                            error={Number(line.receiveQty || 0) - Number(line.RemainingQty || 0) > EPSILON ? 'Too high' : undefined}
                          />
                        </td>
                        <td style={{ padding: '6px 8px 6px 0', verticalAlign: 'top' }}>
                          {Number(line.produktnr || 0) > 49 ? (
                            (line.ExistingStockplaatsen || []).length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {(line.ExistingStockplaatsen || []).slice(0, 4).map((location) => (
                                  <Text key={`${line.BestellingLijnID}-${location.Stockplaats}`} as="span" tone="subdued" variant="bodySm">
                                    {getStockplaceName(location.Stockplaats, location.pickingplace)} ({formatQty(location.CurrentQty)})
                                  </Text>
                                ))}
                                {(line.ExistingStockplaatsen || []).length > 4 ? (
                                  <Text as="span" tone="subdued" variant="bodySm">
                                    +{(line.ExistingStockplaatsen || []).length - 4} more
                                  </Text>
                                ) : null}
                              </div>
                            ) : (
                              <Text as="span" tone="subdued">
                                No stock yet
                              </Text>
                            )
                          ) : (
                            <Text as="span" tone="subdued">
                              -
                            </Text>
                          )}
                        </td>
                        <td style={{ padding: '6px 0 6px 0' }}>
                          {Number(line.produktnr || 0) > 49 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Text as="span" tone="subdued">
                                -&gt;
                              </Text>
                              <div style={{ minWidth: '240px' }}>
                                {(() => {
                                  const options = getLineStockplaceOptions(line);
                                  const currentValue = line.SuggestedStockplaatsID === null || line.SuggestedStockplaatsID === undefined ? '' : String(line.SuggestedStockplaatsID);
                                  const safeValue = options.some((option) => option.value === currentValue) ? currentValue : options[0]?.value || '';
                                  return <Select label="" options={options} value={safeValue} onChange={(value) => updateReceiveLine(index, 'SuggestedStockplaatsID', value)} />;
                                })()}
                              </div>
                            </div>
                          ) : (
                            <Text as="span" tone="subdued">
                              -
                            </Text>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}
